import {
	compileDashboardRuntime,
	useGetDashboard,
	useDashboardRuntimeModules,
} from "@/features/dashboard-runtime";
import { Spinner, VStack } from "@/shared/ui/primitives";
import { createRoute } from "@tanstack/react-router";
import * as React from "react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { workflowIdRoute } from "../route";

class RuntimeErrorBoundary extends React.Component<
	{ children: React.ReactNode; errorTitle: string },
	{ error: Error | null }
> {
	constructor(props: { children: React.ReactNode; errorTitle: string }) {
		super(props);
		this.state = { error: null };
	}

	static getDerivedStateFromError(error: Error) {
		return { error };
	}

	override componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
		console.error("[DashboardRuntime] Render error:", error, errorInfo);
	}

	override render() {
		if (this.state.error) {
			return (
				<VStack className="w-full items-start gap-[12px] rounded-[20px] border border-dg-strong/30 bg-dg-weak/40 p-[20px]">
					<span className="text-sm font-semibold text-dg-strong">{this.props.errorTitle}</span>
					<span className="text-sm text-ct-regular">{this.state.error.message}</span>
				</VStack>
			);
		}

		return this.props.children;
	}
}

const SANDBOX_STYLES = `
@layer dashboard-reset {
	.dashboard-sandbox div,
	.dashboard-sandbox button,
	.dashboard-sandbox form,
	.dashboard-sandbox input,
	.dashboard-sandbox textarea,
	.dashboard-sandbox select,
	.dashboard-sandbox hr {
		border: none;
		outline: none;
	}
	.dashboard-sandbox button,
	.dashboard-sandbox form {
		display: revert;
		align-items: revert;
		justify-content: revert;
	}
	.dashboard-sandbox * {
		scrollbar-width: auto;
	}
	.dashboard-sandbox *::-webkit-scrollbar {
		display: block;
	}
}
[data-theme="dark"] .dashboard-sandbox ::-webkit-calendar-picker-indicator {
	filter: invert(1);
}
.dashboard-sandbox {
	--surface: rgba(255, 255, 255, 1);
	--bg-weak: rgba(252, 252, 253, 1);
	--bg-regular: rgba(241, 241, 244, 1);
	--bg-strong: rgba(228, 228, 231, 1);
	--bg-inverted: rgba(33, 33, 38, 1);
	--ct-weak: rgba(179, 179, 188, 1);
	--ct-regular: rgba(115, 115, 130, 1);
	--ct-strong: rgba(33, 33, 38, 1);
	--ct-inverted: rgba(252, 252, 253, 1);
	--ct-elevated: rgba(252, 252, 253, 1);
	--divider: rgba(228, 228, 231, 1);
	--outline: rgba(222, 222, 227, 1);
	--hover: rgba(247, 247, 248, 1);
	--dim-weak: rgba(37, 37, 42, 0.08);
	--dim-strong: rgba(37, 37, 42, 0.2);
	--pm-weak: rgba(233, 107, 53, 0.08);
	--pm-regular: rgba(233, 107, 53, 0.6);
	--pm-strong: rgba(233, 107, 53, 1);
	--sc-weak: rgba(13, 211, 27, 0.08);
	--sc-regular: rgba(13, 211, 27, 0.6);
	--sc-strong: rgba(13, 211, 27, 1);
	--wn-weak: rgba(242, 178, 2, 0.08);
	--wn-regular: rgba(242, 178, 2, 0.6);
	--wn-strong: rgba(242, 178, 2, 1);
	--dg-weak: rgba(242, 84, 44, 0.08);
	--dg-regular: rgba(242, 84, 44, 0.6);
	--dg-strong: rgba(242, 84, 44, 1);
	font-family: ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, sans-serif;
	line-height: 1.5;
	color: var(--ct-strong);
	background-color: var(--bg-regular);
}
[data-theme="dark"] .dashboard-sandbox {
	--surface: rgba(39, 39, 44, 1);
	--bg-weak: rgba(33, 33, 38, 1);
	--bg-regular: rgba(24, 24, 27, 1);
	--bg-strong: rgba(57, 57, 65, 1);
	--bg-inverted: rgba(252, 252, 253, 1);
	--ct-weak: rgba(96, 96, 108, 1);
	--ct-regular: rgba(158, 158, 169, 1);
	--ct-strong: rgba(252, 252, 253, 1);
	--ct-inverted: rgba(33, 33, 38, 1);
	--ct-elevated: rgba(252, 252, 253, 1);
	--divider: rgba(53, 53, 59, 1);
	--outline: rgba(58, 58, 65, 1);
	--hover: rgba(28, 28, 33, 1);
	--dim-weak: rgba(37, 37, 42, 0.12);
	--dim-strong: rgba(37, 37, 42, 0.24);
	--pm-weak: rgba(233, 107, 53, 0.12);
	--pm-regular: rgba(233, 107, 53, 0.64);
	--pm-strong: rgba(233, 107, 53, 1);
	--sc-weak: rgba(13, 211, 27, 0.12);
	--sc-regular: rgba(13, 211, 27, 0.64);
	--sc-strong: rgba(13, 211, 27, 1);
	--wn-weak: rgba(242, 178, 2, 0.12);
	--wn-regular: rgba(242, 178, 2, 0.64);
	--wn-strong: rgba(242, 178, 2, 1);
	--dg-weak: rgba(242, 84, 44, 0.12);
	--dg-regular: rgba(242, 84, 44, 0.64);
	--dg-strong: rgba(242, 84, 44, 1);
}
`;

const DashboardRouteComponent = () => {
	const { t } = useTranslation();
	const { id } = workflowIdDashboardRoute.useParams();
	const { data, isLoading, isError } = useGetDashboard(id);
	const modules = useDashboardRuntimeModules(id);

	const source = data?.code ?? "";

	const compilation = useMemo(
		() => (source ? compileDashboardRuntime(source, modules) : null),
		[source, modules],
	);

	if (isLoading) {
		return (
			<VStack className="min-h-screen w-full items-center justify-center">
				<Spinner size="lg" />
			</VStack>
		);
	}

	if (isError || !source) {
		return (
			<VStack className="min-h-screen w-full items-center justify-center p-[24px]">
				<VStack className="w-full max-w-[640px] items-center gap-[8px] rounded-[20px] border border-outline bg-bg-weak p-[20px]">
					<span className="text-sm text-ct-regular">
						{isError ? t("workflow.dashboardLoadFailed") : t("workflow.dashboardNoData")}
					</span>
				</VStack>
			</VStack>
		);
	}

	if (compilation?.error) {
		return (
			<VStack className="min-h-screen w-full items-center justify-center p-[24px]">
				<VStack className="w-full max-w-[640px] items-start gap-[8px] rounded-[20px] border border-dg-strong/30 bg-dg-weak/20 p-[20px]">
					<span className="text-sm font-semibold text-dg-strong">{t("workflow.dashboardCompileFailed")}</span>
					<span className="text-sm text-ct-regular">{compilation.error}</span>
				</VStack>
			</VStack>
		);
	}

	if (!compilation?.component) {
		return null;
	}

	const CompiledComponent = compilation.component;

	return (
		<>
			{/* biome-ignore lint: sandbox CSS injection */}
			<style dangerouslySetInnerHTML={{ __html: SANDBOX_STYLES }} />
			<div className="dashboard-sandbox flex min-h-screen w-full flex-col items-center px-[16px] py-[24px] md:p-[24px]">
				<div className="flex w-full max-w-[960px] flex-col">
					<RuntimeErrorBoundary errorTitle={t("workflow.dashboardRuntimeError")}>
						<CompiledComponent />
					</RuntimeErrorBoundary>
				</div>
			</div>
		</>
	);
};

export const workflowIdDashboardRoute = createRoute({
	getParentRoute: () => workflowIdRoute,
	path: "/dashboard",
	component: DashboardRouteComponent,
});
